import React, { useCallback, useMemo } from "react";
import moment from "moment-timezone";

import ModalField, {
	EditButtonRenderProps
} from "components/ModalField/ModalField";
import FieldHourOfDay from "components/FieldHourOfDay";
import { formatAsTime } from "utils/datesAndTimes/formatAsTime";
import { useModalField } from "hooks/useModalField";

interface ModalFieldHourOfDayProps {
	onCancel?: () => void;
	onOK: (newValue: number | undefined) => void;
	title: string;
	helptext?: string;
	value: number | undefined;
	requireSelectedTime: boolean;
	isUnlocked?: boolean;
	formatSelection?: (value: number | undefined) => string;
	editButton?: (props: EditButtonRenderProps) => React.ReactNode;
	isDisabled?: (value: number) => boolean;
}

const ModalFieldHourOfDay: React.FunctionComponent<
	ModalFieldHourOfDayProps
> = ({
	onCancel = () => undefined,
	onOK,
	title,
	helptext,
	value,
	requireSelectedTime,
	isUnlocked = true,
	formatSelection = value =>
		value === undefined ? "" : formatAsTime(moment.duration(value, "hours")),
	editButton,
	isDisabled
}) => {
	const {
		currentValue: selectedTime,
		setCurrentValue: setSelectedTime,
		isOpen,
		setIsOpen,
		toggleIsOpen
	} = useModalField({ value });

	const handleOK = useCallback(() => {
		if (!!requireSelectedTime && selectedTime === undefined) {
			return;
		}
		onOK(selectedTime);
		setIsOpen(false);
	}, [onOK, selectedTime, setIsOpen, requireSelectedTime]);

	const handleCancel = useCallback(() => {
		onCancel();
		setIsOpen(false);
	}, [onCancel, setIsOpen]);

	const displayValue = useMemo(() => formatSelection(selectedTime), [
		selectedTime,
		formatSelection
	]);

	return (
		<ModalField
			isUnlocked={isUnlocked}
			toggleIsOpen={toggleIsOpen}
			isOpen={isOpen}
			handleOK={handleOK}
			handleCancel={handleCancel}
			title={title}
			helptext={helptext}
			displayValue={displayValue}
			editButton={editButton}
		>
			<FieldHourOfDay
				selectedTime={selectedTime}
				onChange={newValue => {
					setSelectedTime(newValue);
				}}
				isDisabled={isDisabled}
			/>
		</ModalField>
	);
};

export default ModalFieldHourOfDay;
